import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../api';
import type { ApiErrorResponse, ApiResponse } from '../../types';
import { apiDataResponseMapper, apiPayloadMapper } from '../../utils';
import type {
  CreateDrillBlastPayload,
  DrillBlastRequestHistoryItem,
  DrillBlastRequestResponse,
  DrillBlastRequestResponseData,
} from './types';

export const createDrillBlastRequest = async (
  payload: CreateDrillBlastPayload,
): Promise<ApiResponse> => {
  const mappedPayload = apiPayloadMapper(payload);
  return await api.post<void, ApiResponse>(
    '/workspaces/drill-and-blast/requests/',
    mappedPayload,
  );
};

export const getDrillBlastRequestList = async (
  iterationId: string,
): Promise<ApiResponse<DrillBlastRequestHistoryItem[]>> => {
  const response = await api.get<
    DrillBlastRequestResponseData[],
    ApiResponse<DrillBlastRequestResponseData[]>
  >('/workspaces/drill-and-blast/requests/', {
    params: { iteration_id: iterationId },
  });

  const normalizedList = apiDataResponseMapper<
    DrillBlastRequestResponseData[],
    DrillBlastRequestResponse[]
  >(response.data);

  return {
    ...response,
    data: normalizedList as DrillBlastRequestHistoryItem[],
  } as ApiResponse<DrillBlastRequestHistoryItem[]>;
};

export const useCreateDrillBlastRequest = () =>
  useMutation<ApiResponse, ApiErrorResponse, CreateDrillBlastPayload>({
    mutationFn: createDrillBlastRequest,
  });

export const useDrillBlastRequestList = (
  iterationId: string | null,
  enabled: boolean,
) =>
  useQuery<ApiResponse<DrillBlastRequestHistoryItem[]>, ApiErrorResponse>({
    queryKey: ['drillBlastRequests', iterationId],
    queryFn: () => getDrillBlastRequestList(iterationId as string),
    enabled,
  });
